import {
  hasDigits,
  hasLowercaseCharacters,
  hasSpecialCharacters,
  hasUppercaseCharacters
} from '../string-utils/utils';

/**
 * Min length and max lengh values.
 * @see https://docs.aws.amazon.com/cognito/latest/developerguide/user-pool-settings-policies.html
 */
const MIN_LENGTH = 9;
const MAX_LENGTH = 100;

export interface Validation {
  length?: boolean;
  uppercase?: boolean;
  lowercase?: boolean;
  numbers?: boolean;
  specialCharacters?: boolean;
}

export interface PasswordValidation {
  isValid: boolean;
  validation: Validation;
}

/**
 * Checks if the password length is between the min length and max length set as parameter.
 * @param {string} password
 * @param {number} minLength
 * @param {number} maxLength
 * @returns {boolean}
 */
const checkPasswordLength = (password: string, minLength: number, maxLength: number): boolean => password.length >= minLength && password.length <= maxLength;

/**
 * Creates a password validator from the configuration passed as paramenters.
 * @param {boolean} [uppercase=true] - If it's true, the password must contain at least one uppercase character.
 * @param {boolean} [lowercase=true] - If it's true, the password must contain at least one lowercase character.
 * @param {boolean} [numbers=true] - If it's true, the password must contain at least one number.
 * @param {boolean} [specialCharacters=true] - If it's true, the password must contain at least one special character.
 * @param {number} [length=MAX_LENGTH] - Max length
 * @returns {false}
 */
export const createPasswordValidator = (
  uppercase = true,
  lowercase = true,
  numbers = true,
  specialCharacters = true,
  length: number = MAX_LENGTH
) => (password: string): PasswordValidation => {

  const validations: PasswordValidation = {
    isValid: false,
    validation: {}
  };

  // The length is gonna be always checked.
  validations.validation.length = checkPasswordLength(password, MIN_LENGTH, length);

  if (uppercase) {
    validations.validation.uppercase = hasUppercaseCharacters(password);
  }

  if (lowercase) {
    validations.validation.lowercase = hasLowercaseCharacters(password);
  }

  if (numbers) {
    validations.validation.numbers = hasDigits(password);
  }

  if (specialCharacters) {
    validations.validation.specialCharacters = hasSpecialCharacters(password);
  }

  validations.isValid = Object.values(validations.validation).every(Boolean);

  return validations;
};
